import axios from 'axios';

export interface SubscriptionPlan {
  tier: string;
  name: string;
  max_teams: number;
  monthly_price_id: string | null;
  annual_price_id: string | null;
}

export interface SubscriptionPlansResponse {
  plans: SubscriptionPlan[];
}

export interface CheckoutSessionRequest {
  tier: string;
  billing_cycle: string;
  coupon_code?: string | null;
}

export interface CheckoutSessionResponse {
  session_id: string;
  url: string;
}

export interface CouponValidationRequest {
  coupon_code: string;
}

export interface CouponValidationResponse {
  valid: boolean;
  discount_percent?: number | null;
  discount_amount?: number | null;
  name?: string | null;
  duration?: string | null;
  error?: string | null;
}

export interface PortalSessionRequest {
  return_url: string;
}

export interface PortalSessionResponse {
  url: string;
}

export interface SubscriptionUpgradeRequest {
  tier: string;
  billing_cycle: string;
}

/**
 * Get available subscription plans
 */
export async function getSubscriptionPlans(): Promise<SubscriptionPlansResponse> {
  try {
    const response = await axios.get<SubscriptionPlansResponse>('/subscriptions/plans');
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const errorMessage = error.response.data?.detail || error.response.data?.message || 'Failed to fetch subscription plans';
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('Network error: Could not reach server');
    } else {
      throw new Error(error.message || 'Failed to fetch subscription plans');
    }
  }
}

/**
 * Validate a coupon code
 */
export async function validateCoupon(couponCode: string): Promise<CouponValidationResponse> {
  try {
    const response = await axios.post<CouponValidationResponse>(
      '/subscriptions/validate-coupon',
      { coupon_code: couponCode } as CouponValidationRequest
    );
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const errorMessage = error.response.data?.detail || error.response.data?.message || 'Failed to validate coupon';
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('Network error: Could not reach server');
    } else {
      throw new Error(error.message || 'Failed to validate coupon');
    }
  }
}

/**
 * Create a checkout session
 */
export async function createCheckoutSession(
  tier: string,
  billingCycle: string,
  couponCode?: string | null
): Promise<CheckoutSessionResponse> {
  try {
    const response = await axios.post<CheckoutSessionResponse>(
      '/subscriptions/checkout',
      {
        tier,
        billing_cycle: billingCycle,
        coupon_code: couponCode || null,
      } as CheckoutSessionRequest
    );
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const errorMessage = error.response.data?.detail || error.response.data?.message || 'Failed to create checkout session';
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('Network error: Could not reach server');
    } else {
      throw new Error(error.message || 'Failed to create checkout session');
    }
  }
}

/**
 * Create a customer portal session
 */
export async function createPortalSession(returnUrl: string): Promise<PortalSessionResponse> {
  try {
    const response = await axios.post<PortalSessionResponse>(
      '/subscriptions/portal',
      { return_url: returnUrl } as PortalSessionRequest
    );
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const errorMessage = error.response.data?.detail || error.response.data?.message || 'Failed to create portal session';
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('Network error: Could not reach server');
    } else {
      throw new Error(error.message || 'Failed to create portal session');
    }
  }
}

/**
 * Upgrade subscription tier
 */
export async function upgradeSubscription(
  tier: string,
  billingCycle: string
): Promise<void> {
  try {
    await axios.post(
      '/subscriptions/upgrade',
      {
        tier,
        billing_cycle: billingCycle,
      } as SubscriptionUpgradeRequest
    );
  } catch (error: any) {
    if (error.response) {
      const errorMessage = error.response.data?.detail || error.response.data?.message || 'Failed to upgrade subscription';
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('Network error: Could not reach server');
    } else {
      throw new Error(error.message || 'Failed to upgrade subscription');
    }
  }
}

/**
 * Cancel subscription
 */
export async function cancelSubscription(): Promise<void> {
  try {
    await axios.post('/subscriptions/cancel');
  } catch (error: any) {
    if (error.response) {
      const errorMessage = error.response.data?.detail || error.response.data?.message || 'Failed to cancel subscription';
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('Network error: Could not reach server');
    } else {
      throw new Error(error.message || 'Failed to cancel subscription');
    }
  }
}

/**
 * Resume cancelled subscription
 */
export async function resumeSubscription(): Promise<void> {
  try {
    await axios.post('/subscriptions/resume');
  } catch (error: any) {
    if (error.response) {
      const errorMessage = error.response.data?.detail || error.response.data?.message || 'Failed to resume subscription';
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('Network error: Could not reach server');
    } else {
      throw new Error(error.message || 'Failed to resume subscription');
    }
  }
}

